import { useEffect, useState } from 'react';
import { Activity, Radar as RadarIcon, LineChart as LineChartIcon, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { snapshotsAPI } from '../api/client';
import type { ForumHealthMetric, ForumHealthRadarPoint, OrgHealthTrendResponse } from '../types';
import {
  DualMetricRadar,
  DualMetricCategoryTrendChart,
  currentYearMonth,
  previousYearMonth,
  formatYearMonth,
} from './forumHealth';

// Org-wide State/Direction health card — the same radar/line pair the forum
// dashboard shows for one forum, aggregated across every active user, with an
// "Organization / By forum" scope so an admin can zoom into one forum's
// averages without leaving the analytics page. No "by member" mode here: the
// org dashboard is deliberately anonymized (per-member charts live on the
// forum dashboard).
const SNAPSHOT_TYPE_LABELS: Record<string, string> = {
  member: 'members',
  advisor: 'advisors',
  leader: 'leaders',
  nonprofit: 'nonprofit',
};

export default function OrgHealthCard({
  forums,
  snapshotType,
}: {
  // Forums offered in the "By forum" picker (id + display name).
  forums: { id: number; name: string }[];
  snapshotType?: string;
}) {
  const [viewMode, setViewMode] = useState<'radar' | 'line'>('radar');
  // null = organization-wide; otherwise the selected forum's id.
  const [scopeForumId, setScopeForumId] = useState<number | null>(null);
  const [radarMonth, setRadarMonth] = useState(currentYearMonth());
  const [radarData, setRadarData] = useState<Record<ForumHealthMetric, ForumHealthRadarPoint[] | null>>({
    state: null,
    direction: null,
  });
  const [radarLoading, setRadarLoading] = useState(false);
  const [trendMonths, setTrendMonths] = useState(6);
  const [trendData, setTrendData] = useState<Record<ForumHealthMetric, OrgHealthTrendResponse | null>>({
    state: null,
    direction: null,
  });
  const [trendLoading, setTrendLoading] = useState(false);
  const [hiddenCategories, setHiddenCategories] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (scopeForumId !== null && !forums.some((f) => f.id === scopeForumId)) {
      setScopeForumId(null);
    }
  }, [forums, scopeForumId]);

  useEffect(() => {
    if (viewMode !== 'radar') return;
    let cancelled = false;
    setRadarLoading(true);
    const fetchRadar = (metric: ForumHealthMetric) =>
      scopeForumId
        ? snapshotsAPI.getForumHealthRadar(
            scopeForumId, radarMonth, metric, undefined, snapshotType,
          )
        : snapshotsAPI.getOrgHealthRadar(radarMonth, metric, snapshotType);
    Promise.all([fetchRadar('state'), fetchRadar('direction')])
      .then(([stateResult, directionResult]) => {
        if (!cancelled) {
          setRadarData({ state: stateResult.health_radar, direction: directionResult.health_radar });
        }
      })
      .catch(() => { if (!cancelled) toast.error("Couldn't load the health radar"); })
      .finally(() => { if (!cancelled) setRadarLoading(false); });
    return () => { cancelled = true; };
  }, [viewMode, radarMonth, scopeForumId, snapshotType]);

  useEffect(() => {
    if (viewMode !== 'line') return;
    let cancelled = false;
    setTrendLoading(true);
    const fetchTrend = (metric: ForumHealthMetric) =>
      scopeForumId
        ? snapshotsAPI.getForumHealthTrend(scopeForumId, metric, trendMonths, snapshotType)
        : snapshotsAPI.getOrgHealthTrend(metric, trendMonths, snapshotType);
    Promise.all([fetchTrend('state'), fetchTrend('direction')])
      .then(([stateResult, directionResult]) => {
        if (!cancelled) setTrendData({ state: stateResult, direction: directionResult });
      })
      .catch(() => { if (!cancelled) toast.error("Couldn't load the health trend"); })
      .finally(() => { if (!cancelled) setTrendLoading(false); });
    return () => { cancelled = true; };
  }, [viewMode, trendMonths, scopeForumId, snapshotType]);

  const toggleCategory = (category: string) => {
    setHiddenCategories((prev) => {
      const next = new Set(prev);
      if (next.has(category)) {
        next.delete(category);
      } else {
        next.add(category);
      }
      return next;
    });
  };

  const isRadarCurrentMonth = radarMonth === currentYearMonth();
  const currentLabel = isRadarCurrentMonth ? 'This month' : formatYearMonth(radarMonth);
  const previousLabel = isRadarCurrentMonth ? 'Last month' : formatYearMonth(previousYearMonth(radarMonth));
  const scopeForum = forums.find((f) => f.id === scopeForumId);
  const typeLabel = snapshotType ? SNAPSHOT_TYPE_LABELS[snapshotType] : null;
  const scopeLabel = scopeForum
    ? `${scopeForum.name} average${typeLabel ? ` (${typeLabel})` : ''}`
    : typeLabel
      ? `Org-average (${typeLabel})`
      : 'Org-average';

  const statePoints = radarData.state ?? [];
  const directionPoints = radarData.direction ?? [];
  const radarHasData = statePoints.some((p) => p.current_avg !== null || p.previous_avg !== null)
    || directionPoints.some((p) => p.current_avg !== null || p.previous_avg !== null);
  const chipCategories = viewMode === 'radar'
    ? Array.from(new Set([...statePoints, ...directionPoints].map((p) => p.category)))
    : Array.from(new Set([
        ...(trendData.state?.categories.map((c) => c.category) ?? []),
        ...(trendData.direction?.categories.map((c) => c.category) ?? []),
      ]));
  const allHidden = chipCategories.length > 0 && chipCategories.every((c) => hiddenCategories.has(c));
  const isLoading = viewMode === 'radar' ? radarLoading : trendLoading;

  return (
    <div className="bg-white rounded-2xl p-6 shadow-sm">
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-3 mb-3">
        <div>
          <h2 className="font-semibold text-navy flex items-center gap-2 mb-1">
            <Activity size={20} className="text-gold" />
            Organization Health
          </h2>
          <p className="text-sm text-gray-500">
            {viewMode === 'radar'
              ? `${scopeLabel} State & Direction by area, ${currentLabel.toLowerCase()} vs ${previousLabel.toLowerCase()}.`
              : `${scopeLabel} State & Direction by area, last ${trendMonths} months.`}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex rounded-lg border border-gray-200 overflow-hidden" role="group" aria-label="Chart view">
            <button
              onClick={() => setViewMode('radar')}
              aria-pressed={viewMode === 'radar'}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium transition ${
                viewMode === 'radar' ? 'bg-navy text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
              }`}
            >
              <RadarIcon size={14} /> Radar
            </button>
            <button
              onClick={() => setViewMode('line')}
              aria-pressed={viewMode === 'line'}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium transition ${
                viewMode === 'line' ? 'bg-navy text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
              }`}
            >
              <LineChartIcon size={14} /> Line
            </button>
          </div>
          {/* Organization vs one forum's averages. */}
          <div className="flex rounded-lg border border-gray-200 overflow-hidden" role="group" aria-label="Chart data source">
            <button
              onClick={() => setScopeForumId(null)}
              aria-pressed={scopeForumId === null}
              className={`px-3 py-1.5 text-sm font-medium transition ${
                scopeForumId === null ? 'bg-navy text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
              }`}
            >
              Organization
            </button>
            <button
              onClick={() => setScopeForumId((prev) => prev ?? forums[0]?.id ?? null)}
              aria-pressed={scopeForumId !== null}
              disabled={forums.length === 0}
              className={`px-3 py-1.5 text-sm font-medium transition disabled:opacity-50 ${
                scopeForumId !== null ? 'bg-navy text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
              }`}
            >
              By forum
            </button>
          </div>
          {scopeForumId !== null && (
            <select
              value={scopeForumId}
              onChange={(e) => setScopeForumId(Number(e.target.value))}
              className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-navy/30"
              aria-label="Forum"
            >
              {forums.map((f) => (
                <option key={f.id} value={f.id}>{f.name}</option>
              ))}
            </select>
          )}
          {viewMode === 'radar' ? (
            <input
              type="month"
              value={radarMonth}
              max={currentYearMonth()}
              onChange={(e) => { if (e.target.value) setRadarMonth(e.target.value); }}
              className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-navy/30"
              aria-label="Radar month"
            />
          ) : (
            <select
              value={trendMonths}
              onChange={(e) => setTrendMonths(Number(e.target.value))}
              className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-navy/30"
              aria-label="Trend months"
            >
              {[3, 6, 12].map((n) => (
                <option key={n} value={n}>Last {n} months</option>
              ))}
            </select>
          )}
        </div>
      </div>
      <div className="relative">
        {viewMode === 'radar' && !radarLoading && !radarHasData ? (
          <div className="py-8 flex items-center justify-center">
            <p className="text-sm text-gray-400 italic text-center">
              No snapshots for {currentLabel.toLowerCase()} yet — the health radar will
              appear once members submit.
            </p>
          </div>
        ) : viewMode === 'radar' && allHidden ? (
          <div className="h-80 flex items-center justify-center">
            <p className="text-sm text-gray-400 italic">
              All areas hidden — turn an area back on below to see the radar.
            </p>
          </div>
        ) : viewMode === 'radar' ? (
          <DualMetricRadar
            statePoints={statePoints.filter((p) => !hiddenCategories.has(p.category))}
            directionPoints={directionPoints.filter((p) => !hiddenCategories.has(p.category))}
            currentLabel={currentLabel}
            previousLabel={previousLabel}
          />
        ) : (
          <DualMetricCategoryTrendChart
            stateTrend={trendData.state}
            directionTrend={trendData.direction}
            hiddenCategories={hiddenCategories}
            emptyLabel="No snapshots yet for this period — the trend will appear here."
          />
        )}
        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-white/60 rounded-xl">
            <Loader2 size={28} className="animate-spin text-navy" />
          </div>
        )}
      </div>
      {chipCategories.length > 0 && !(viewMode === 'radar' && !radarLoading && !radarHasData) && (
        <div className="flex flex-wrap gap-1.5 mt-3">
          {chipCategories.map((cat) => {
            const hidden = hiddenCategories.has(cat);
            return (
              <button
                key={cat}
                onClick={() => toggleCategory(cat)}
                aria-pressed={!hidden}
                className={`px-2.5 py-1 rounded-full text-xs font-medium border transition ${
                  hidden
                    ? 'bg-gray-50 text-gray-400 border-gray-200 line-through'
                    : 'bg-navy/5 text-navy border-navy/20 hover:bg-navy/10'
                }`}
              >
                {cat}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
